import { memo, type MouseEvent, useCallback, useRef } from 'react'

import { useGalleryDisplay } from '@/components/layout/Gallery'
import { type RecType } from '@/defs/rec'
import { useCardModalButton } from '@/hooks/useCardModalButton'
import { useCardsRec } from '@/hooks/useCardsRec'
import { useInView } from '@/hooks/useInView'
import { cn } from '@/utils/commonUtil'

import { CardControls } from '../CardControls/CardControls'
import { CardFlags } from '../CardFlags/CardFlags'

import styles from './CardRec.module.scss'
import { CardRecFaces } from './CardRecFaces/CardRecFaces'
import { CardRecStats } from './CardRecStats/CardRecStats'

type Props = {
  assumeInView?: boolean
  clickOpensModal?: boolean
  controls?: 'left' | 'right' | 'none'
  disableLink?: boolean
  disableModal?: boolean
  extend?: boolean
  flagsHide?: boolean
  forceLoad?: boolean
  href?: string
  rec: RecType
  showPrices?: boolean
  size?: 'sm'
  statsHide?: boolean
}

const CardRecInner = ({
  assumeInView,
  clickOpensModal,
  controls = 'right',
  disableLink,
  disableModal,
  extend,
  flagsHide,
  forceLoad,
  href: hrefProp,
  rec,
  showPrices,
  size,
  statsHide,
}: Props) => {
  const { stats: statsGallery, statsRoles } = useGalleryDisplay()
  const stats = (rec.role && statsRoles?.[rec.role]) || statsGallery
  const { inViewStatus, ref } = useInView({ assumeInView, forceLoad })
  const { onlyBack, onlyFront, onlyPartner } = rec
  const { card } = useCardsRec({ enabled: inViewStatus !== 'outside', rec })
  const hasContentWarning = !!card?.hasContentWarning
  const sideRef = useRef<HTMLDivElement>(null)

  const { cardLink, onClick: openModal } = useCardModalButton({ rec })
  const href = disableLink ? undefined : hrefProp ? hrefProp : cardLink

  const isPartner = !!rec.partnerRef
  const isDfc = !!card?.nameBack && !!card.imageBack

  const isInView = inViewStatus === 'inView'
  const showFlags = isInView && !hasContentWarning && !flagsHide && controls !== 'none'
  const hasStats = !statsHide && !!stats?.length && controls !== 'none'
  const showStats = isInView && !hasContentWarning && hasStats
  const showControls = isInView && !hasContentWarning && controls !== 'none'

  const onFacesClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      if (disableModal || e.defaultPrevented) return
      const isTiny = !!sideRef.current && getComputedStyle(sideRef.current).display === 'none'
      if (clickOpensModal || (controls !== 'none' && isTiny)) openModal(e)
    },
    [clickOpensModal, controls, disableModal, openModal],
  )

  return (
    <div
      className={cn(styles.container, {
        controls,
        extend,
        hasStats,
        isDfc,
        isPartner,
        onlyBack,
        onlyFront,
        onlyPartner,
        size,
        styles,
      })}
      ref={ref}
    >
      <div className={styles.inner}>
        <div className={styles.faces}>
          <CardRecFaces
            animate={!forceLoad}
            href={href}
            inViewStatus={inViewStatus}
            onClick={onFacesClick}
            rec={rec}
            showPrices={showPrices}
          />
        </div>
        <div className={styles.side} ref={sideRef}>
          {showFlags && <CardFlags controls={controls} rec={rec} />}
          {showControls && <CardControls controls={controls} disableModal={disableModal} rec={rec} />}
        </div>
        {showStats && <CardRecStats rec={rec} size={size} stats={stats} />}
      </div>
    </div>
  )
}

export const CardRec = memo(CardRecInner)
